import { ArrowLeft, FileCheck, ReceiptText, Sparkles } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { useEmployee } from '@/features/hrm/employees/queries';
import { employeeName } from '@/features/hrm/employees/types';
import { formatMoney } from '@/lib/money';
import { AddPayslipForm } from '../components/AddPayslipForm';
import { payrollStatusLabel, payrollStatusVariant } from '../constants';
import { useFinalizeRun, useGenerateFromInvoices, usePayslips, useRun } from '../queries';

function EmployeeName({ id }: { id: number }) {
  const { data } = useEmployee(id);
  return <>{data ? employeeName(data) : `#${id}`}</>;
}

export function PayrollDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();

  // Resolve the run by its public_id (URL-safe, ADR-041); payslips + finalize
  // hang off the resolved integer pk.
  const { data: run, isPending, isError, error } = useRun(publicId);
  const payslips = usePayslips(run?.id ?? -1);
  const finalize = useFinalizeRun(publicId);
  const generate = useGenerateFromInvoices(publicId, run?.id ?? -1);

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/finance/payroll')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');
  const isDraft = run.status === 'draft';
  const slips = payslips.data?.results ?? [];

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/finance/payroll')}>
            <ArrowLeft />
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">
            {fmt(run.period_start)} – {fmt(run.period_end)}
          </h1>
          <Badge variant={payrollStatusVariant(run.status)}>{payrollStatusLabel(run.status)}</Badge>
        </div>
        {isDraft ? (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => generate.mutate()}
              disabled={generate.isPending}
            >
              <Sparkles /> {generate.isPending ? 'Generating…' : 'Generate from invoices'}
            </Button>
            <Button onClick={() => finalize.mutate()} disabled={finalize.isPending}>
              <FileCheck /> {finalize.isPending ? 'Finalizing…' : 'Finalize'}
            </Button>
          </div>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Payslips</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="hidden sm:table-cell">Source</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="hidden text-right sm:table-cell">Deductions</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead className="hidden text-right md:table-cell">Net (USD)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payslips.isPending ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-6">
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                  </TableRow>
                ) : slips.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground py-8 text-center">
                      No payslips yet. Use “Generate from invoices” to pay signed staff invoices.
                    </TableCell>
                  </TableRow>
                ) : (
                  slips.map((p) => (
                    <TableRow key={p.public_id}>
                      <TableCell>
                        <EmployeeName id={p.employee} />
                      </TableCell>
                      <TableCell className="text-muted-foreground hidden sm:table-cell">
                        {p.staff_invoice ? (
                          <span className="inline-flex items-center gap-1 text-xs">
                            <ReceiptText className="size-3.5" /> Invoice
                          </span>
                        ) : (
                          <span className="text-xs">Manual</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(p.gross, p.currency) ?? '—'}
                      </TableCell>
                      <TableCell className="hidden text-right tabular-nums sm:table-cell">
                        {formatMoney(p.deductions, p.currency) ?? '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(p.net, p.currency) ?? '—'}
                      </TableCell>
                      <TableCell className="hidden text-right tabular-nums md:table-cell">
                        {formatMoney(p.base_net, 'USD') ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {isDraft ? <AddPayslipForm runId={run.id} /> : null}
        </CardContent>
      </Card>
    </div>
  );
}
